import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Card,
  Col,
  Flex,
  FormContainer,
  Icon,
  Row,
  Text,
  Title,
} from '@wyz/ui';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';
import { Trans } from '~/common/context';
import { useBackTo } from '~/common/hooks';
import { RedistributionBusinessRulesCard } from '~/connect/components';
import { RedistributionGenericRuleUtils } from '~/connect/helpers';
import { redistributionGenericRuleFormSchema } from '~/connect/schemas';
import {
  IRedistributionGenericRule,
  IRedistributionGenericRuleForm,
} from '~/connect/types';

type Props = {
  genericRule: IRedistributionGenericRule;
  onSubmit: (data: IRedistributionGenericRuleForm) => void;
  isSubmitting: boolean;
  readOnly: boolean;
};

const RedistributionGenericRuleForm = ({
  genericRule,
  onSubmit,
  isSubmitting,
  readOnly,
}: Props) => {
  const backTo = useBackTo('..');
  const methods = useForm<IRedistributionGenericRuleForm>({
    resolver: zodResolver(redistributionGenericRuleFormSchema),
    defaultValues: RedistributionGenericRuleUtils.rawToForm(genericRule),
  });

  return (
    <FormContainer
      methods={methods}
      submitHandler={(data) => onSubmit(data)}
      className='space-y-2'
    >
      <Card className='mb-section'>
        <Card.Header>
          <Title heading='lg' color='tertiary' weight='bold'>
            <Icon icon='post' width='1.5rem' height='1.5rem' />
            <span className='ms-2'>
              <Trans i18nKey='app.common.general_information' />
            </span>
          </Title>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={12}>
              <Text color='tertiary'>
                <Trans i18nKey='app.common.model_name' />
              </Text>
              <Text heading='normal' weight='semi-bold'>
                {genericRule.name}
              </Text>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <RedistributionBusinessRulesCard disabled={readOnly} />

      <Card>
        <Card.Body>
          <Flex alignItems='center' justifyContent='between'>
            <Button
              id='cancel'
              type='button'
              variant='secondary'
              disabled={isSubmitting}
              as={Link}
              to={backTo}
            >
              <Trans i18nKey='app.common.button.cancel' />
            </Button>
            {!readOnly && (
              <Button
                id='save-redistribution-generic-rule'
                type='submit'
                variant='primary'
                isLoading={isSubmitting}
              >
                <Trans i18nKey='app.common.button.save' />
              </Button>
            )}
          </Flex>
        </Card.Body>
      </Card>
    </FormContainer>
  );
};

export default RedistributionGenericRuleForm;
